import {Component, Inject, OnInit} from '@angular/core';
import { FormControl, FormGroup, FormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {
  ComparableIdentity,
  DuplicatePersonHit,
  duplicatePersonMessage,
  ExistingPersonRef,
  findDuplicatePerson,
  IndividualFormValue,
  roleNeedsCitizenshipResidencyAlert,
  SignerRoleTypes,
} from '@portal-workspace/grow-shared-library';
import {setupUntilDestroy} from '@portal-workspace/grow-ui-library';
import {UntilDestroy} from '@ngneat/until-destroy';
import { Subscription } from 'rxjs';
import {
  IndividualFormDialogData,
  IndividualFormDialogResult,
  individualSignerRole,
} from '@portal-workspace/grow-shared-library';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { FlexModule } from '@angular/flex-layout/flex';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { IndividualFormComponent } from '../individual-form-component/individual-form.component';

@UntilDestroy({arrayName: 'subscriptions'})
@Component({
    templateUrl: './individual-form.dialog.html',
    styleUrls: ['./individual-form.dialog.scss'],
    standalone: true,
    imports: [FormsModule, ReactiveFormsModule, IndividualFormComponent, MatFormFieldModule, MatInputModule, MatSelectModule, MatOptionModule, FlexModule, MatButtonModule, MatButtonToggleModule]
})
export class IndividualFormDialog implements OnInit {

  subscriptions: Subscription[] = [];

  /**
   * The identity a name-only duplicate warning is currently raised against. Held as the identity
   * rather than a flag so editing the name or date of birth afterwards withdraws the warning and
   * raises it afresh on the next Save, while filling in the rest of the form does not.
   */
  private warnedDuplicateIdentity: string | null = null;

  /** Fixed for the dialog's lifetime, so it is built once rather than on every change detection. */
  private existingPeople: ExistingPersonRef[] = [];

  formControlIndividual: FormControl<IndividualFormValue>;
  formControlSignerRole: FormControl<SignerRoleTypes | 'Trustee' | 'Partner'>;
  formControlOwner: FormControl<number | null>;
  formControlResolutionMode: FormControl<'EXISTING' | 'NEW'>;
  formControlExistingPersonIndividualId: FormControl<number | null>;
  formGroup: FormGroup<{
    individual: FormControl<IndividualFormValue>,
    role: FormControl<SignerRoleTypes | 'Trustee' | 'Partner'>,
    owner: FormControl<number | null>,
    resolutionMode: FormControl<'EXISTING' | 'NEW'>,
    existingPersonIndividualId: FormControl<number | null>,
  }>;

  constructor(@Inject(MAT_DIALOG_DATA) public data: IndividualFormDialogData,
              private matDialogRef: MatDialogRef<IndividualFormDialog, IndividualFormDialogResult>,
              private formBuilder: FormBuilder) {
    this.formControlIndividual = formBuilder.control(null, [Validators.required]);
    this.formControlSignerRole = formBuilder.control(null, [Validators.required]);
    this.formControlOwner = formBuilder.control(null);
    this.formControlResolutionMode = formBuilder.control(
      data.existingPersonOptions?.length ? 'EXISTING' : 'NEW',
      {nonNullable: true},
    );
    this.formControlExistingPersonIndividualId = formBuilder.control(null);
    this.formGroup = formBuilder.group({
      individual: this.formControlIndividual,
      role: this.formControlSignerRole,
      owner: this.formControlOwner,
      resolutionMode: this.formControlResolutionMode,
      existingPersonIndividualId: this.formControlExistingPersonIndividualId,
    });
  }

  ngOnInit(): void {
    setupUntilDestroy(this);
    this.existingPeople = this.data.existingIndividuals
      .filter(i => !i.isDeleted)
      .map(i => ({
        givenName: i.givenName,
        middleName: i.middleName,
        surname: i.surname,
        dateOfBirth: i.dateOfBirth,
        // The ternary's fallback was already unreachable: the projection always set SignerRole to one
        // of the four values, as individualSignerRole does.
        location: `an existing ${individualSignerRole(i)} on this application`,
      }));
    if (this.data.prefill) {
      const prefill: IndividualFormValue = {
        title: null,
        firstName: this.data.prefill.firstName ?? '',
        middleName: '',
        lastName: this.data.prefill.lastName ?? '',
        dob: this.data.prefill.dob!,
        hasAlias: false,
        aliasNames: null,
        countryOfCitizenship: null,
        email: '',
        mobileNumber: '',
        residentialAddress: null,
        propertyOwner: null,
        identification: null,
        idDocument: null,
      };
      // Keep the outer control invalid during the first change-detection pass. Setting this object
      // synchronously makes Validators.required pass before <individual-form> registers its stricter
      // validator, which flips the submit button back to disabled and triggers NG0100.
      Promise.resolve().then(() => this.formControlIndividual.setValue(prefill));
    }
    if (this.data.editIndividual) {
      const editIndividual = this.data.editIndividual;
      // Deferred for the same reason as prefill above.
      Promise.resolve().then(() => this.formControlIndividual.setValue(editIndividual));
    }
    if (this.data.initialRole) {
      this.formControlSignerRole.setValue(this.data.initialRole);
    }
    // A forced trustee/partner add pre-selects the single owning entity and locks both controls.
    if (this.data.lockRole) {
      this.formControlSignerRole.disable();
    }
    if (this.data.ownerEntities?.length === 1) {
      this.formControlOwner.setValue(this.data.ownerEntities[0].commercialEntityId);
    }
    // A trustee/partner belongs to an owning entity; require it when one of those roles is chosen.
    this.updateOwnerValidator();
    // EXISTING hides <individual-form>, so disabling it here is safe. NEW renders that child in
    // the same pass; enabling/validating it synchronously causes NG0100 on the submit button.
    if (this.isLinkingExisting()) {
      this.updateResolutionMode();
    }
    this.subscriptions.push(
      this.formControlSignerRole.valueChanges.subscribe(() => this.updateOwnerValidator()),
    );
  }

  isLinkingExisting(): boolean {
    return !!this.data.existingPersonOptions?.length
      && this.formControlResolutionMode.value === 'EXISTING';
  }

  isEditing(): boolean {
    return !!this.data.editIndividual;
  }

  showIdDocumentUpload(): boolean {
    return this.data.showIdDocumentUpload ?? !this.isEditing();
  }

  heading(): string {
    if (this.isEditing()) {
      return 'Edit individual';
    }
    return this.data.existingPersonOptions?.length ? 'Select application person' : 'Add new individual';
  }

  setResolutionMode(mode: 'EXISTING' | 'NEW'): void {
    if (this.formControlResolutionMode.value === mode) return;
    this.formControlResolutionMode.setValue(mode, {emitEvent: false});
    // The NEW branch creates <individual-form>, whose CVA updates this control's validity during
    // initialisation. Enabling it in the same change-detection pass makes the submit button change
    // from enabled to disabled mid-check (NG0100). Let the child initialise first, then enable it.
    Promise.resolve().then(() => this.updateResolutionMode());
  }

  private updateResolutionMode(): void {
    if (this.isLinkingExisting()) {
      this.formControlExistingPersonIndividualId.setValidators([Validators.required]);
      this.formControlIndividual.disable();
    } else {
      this.formControlExistingPersonIndividualId.clearValidators();
      this.formControlIndividual.enable();
      this.formControlIndividual.setValidators([Validators.required]);
    }
    this.formControlExistingPersonIndividualId.updateValueAndValidity();
    this.formControlIndividual.updateValueAndValidity();
  }

  private updateOwnerValidator() {
    if (this.needsOwner()) {
      this.formControlOwner.setValidators([Validators.required]);
    } else {
      this.formControlOwner.clearValidators();
      this.formControlOwner.setValue(null);
    }
    this.formControlOwner.updateValueAndValidity();
  }

  residencyAlertApplies(): boolean {
    return roleNeedsCitizenshipResidencyAlert(this.formControlSignerRole.value);
  }

  /** Trustee/partner roles need an owning trust/partnership; the default signer set does not. */
  needsOwner(): boolean {
    const role = this.formControlSignerRole.value;
    return role === 'Trustee' || role === 'Partner';
  }

  isDuplicateEmail(): boolean {
    const email = (this.formControlIndividual.value?.email ?? '').trim().toLowerCase();
    if (!email) {
      return false;
    }
    return this.data.existingIndividuals
      .filter(i => !i.isDeleted)
      .some(i => (i.email ?? '').trim().toLowerCase() === email);
  }

  isDuplicateMobile(): boolean {
    const mobile = this.formControlIndividual.value?.mobileNumber ?? '';
    if (!mobile) {
      return false;
    }
    return this.data.existingIndividuals
      .filter(i => !i.isDeleted)
      .some(i => i.mobileNumber === mobile);
  }

  /**
   * Whether this person is already on the application, by name and date of birth.
   *
   * Applies to every role but trustee and partner: the same human can be a director of the applicant
   * and a trustee of a guarantor trust, so a party declaration is not a duplicate.
   *
   * Edits are not checked: a duplicate an edit leaves behind, or one a legacy application already
   * holds, is resolved with Merge on the KYC tab rather than by blocking the edit.
   */
  private duplicateHit(): DuplicatePersonHit | null {
    if (this.isEditing() || this.needsOwner()) {
      return null;
    }
    return findDuplicatePerson(this.candidateIdentity(), this.existingPeople);
  }

  private candidateIdentity(): ComparableIdentity {
    const v = this.formControlIndividual.value;
    return {
      givenName: v?.firstName,
      middleName: v?.middleName,
      surname: v?.lastName,
      dateOfBirth: v?.dob ? v.dob.format('YYYY-MM-DD') : null,
    };
  }

  duplicateIdentityError(): string | null {
    const hit = this.duplicateHit();
    return hit?.match === 'exact' ? duplicatePersonMessage(this.candidateIdentity(), hit) : null;
  }

  /**
   * A name match against a record with no date of birth, which the user may confirm past. Withheld
   * until the first Save attempt raises it, so the button only offers to add anyway on a click that
   * actually will.
   */
  duplicateIdentityWarning(): string | null {
    const candidate = this.candidateIdentity();
    if (this.warnedDuplicateIdentity !== this.identityKey(candidate)) {
      return null;
    }
    const hit = this.duplicateHit();
    return hit?.match === 'name-only' ? duplicatePersonMessage(candidate, hit) : null;
  }

  private identityKey(identity: ComparableIdentity): string {
    return `${identity.givenName ?? ''}|${identity.middleName ?? ''}|${identity.surname ?? ''}|${identity.dateOfBirth ?? ''}`;
  }

  /**
   * Whether the save may proceed. A name-only match holds the first Save back to raise the warning
   * and lets the next one through; an exact match never reaches here, disableSubmission() refuses it.
   */
  private acceptDuplicateCheck(): boolean {
    const candidate = this.candidateIdentity();
    const key = this.identityKey(candidate);
    if (this.warnedDuplicateIdentity === key) {
      return true;
    }
    if (this.duplicateHit()?.match !== 'name-only') {
      return true;
    }
    this.warnedDuplicateIdentity = key;
    return false;
  }

  submitLabel(): string {
    if (this.isLinkingExisting()) {
      return 'Use selected person';
    }
    if (this.duplicateIdentityWarning()) {
      return this.isEditing() ? 'Save anyway' : 'Add anyway';
    }
    return this.isEditing() ? 'Save' : 'Add individual';
  }

  onCancel() {
    this.matDialogRef.close({ cancelled: true, valid: this.formGroup.valid });
  }

  onSubmit() {
    if (this.isLinkingExisting()) {
      this.matDialogRef.close({
        cancelled: false,
        valid: this.formGroup.valid,
        applicationIndividualId: this.formControlExistingPersonIndividualId.value ?? undefined,
        selectedRole: this.formControlSignerRole.value as SignerRoleTypes,
      });
      return;
    }
    if (!this.acceptDuplicateCheck()) {
      return;
    }
    // NOTE: when the formGroup is valid, properties under it should not be null
    const v = this.formControlIndividual.value!;
    const signerRole = this.formControlSignerRole.value;
    const isGuarantor = signerRole == 'Guarantor' || signerRole == 'GuarantorSigner';
    const isTrustee = signerRole === 'Trustee';
    const isPartner = signerRole === 'Partner';
    const i: IndividualFormDialogResult = {
      cancelled: false,
      valid: this.formGroup.valid,
      individualFormValue: v,
      individual: {
        title: v.title!,
        dob: v.dob,
        hasAlias: !!v.hasAlias,
        aliasNames: v.hasAlias ? v.aliasNames : null,
        countryOfCitizenship: v.countryOfCitizenship,
        lastName: v.lastName,
        firstName: v.firstName,
        residentialAddress: v.residentialAddress!,
        email: v.email ?? '',
        mobile: v.mobileNumber ?? '',
        middleName: v.middleName ?? '',
        role: (isTrustee ? 'Trustee' : isPartner ? 'Partner' : isGuarantor ? 'Guarantor' : null),
        // Trustee/partner are party roles, not signing roles; the signer role only applies to the
        // default signer/guarantor set.
        signerRole: (isTrustee || isPartner) ? null : signerRole,
        guarantor: isGuarantor,
        identification: v.identification ?? undefined,
        idDocument: v.idDocument ?? null,
        contact: 'contact',
        parentCommercialEntityId: this.needsOwner() ? (this.formControlOwner.value ?? undefined) : undefined,
      }
    };
    this.matDialogRef.close(i);
  }

  disableSubmission(): boolean {
    return (
      this.formGroup.invalid ||
      (!this.isLinkingExisting() && (
        this.isDuplicateEmail() || this.isDuplicateMobile() || this.duplicateHit()?.match === 'exact'
      ))
    );
  }
}
